import clsx from 'clsx';
import { useCalculator } from 'contexts/Calculator/Calculator';
import { useEffect, useMemo, useState } from 'react';
import { Bar } from 'react-chartjs-2';

interface GraphProps {
    className?: string;
    height?: number;
}

export default function Graph({ className, height }: GraphProps) {

    const [backgroundColor, setBackgroundColor] = useState<string[]>([]);
    const [data, setData] = useState<any>([]);
    const [labels, setLabels] = useState<string[]>([]);
    const calculator = useCalculator();

    useEffect(() => {
        // Get background color
        const newBackgroundColor = calculator.variants.map((variant, index) => {
            if (calculator.comparisonResult?.winner?.index === index) {
                return '#76c4b5';
            } else if (calculator.comparisonResult?.isInconclusive && calculator.results[index]?.minValue && calculator.results[index]?.maxValue) {
                return '#DDDEE0';
            } else {
                return '#979797';
            }
        });
        setBackgroundColor(newBackgroundColor);

        // Get labels
        const newLabels = calculator.variants.map(variant => variant.label);
        setLabels(newLabels);

        // Get data
        const newData = calculator.variants.map((variant, index) => {
            const result = calculator.results[index];
            const isSame = result?.minValue === result?.maxValue;
            if (result && !isSame) {
                return [result.minValue, result.maxValue];
            } else if (result && isSame) {
                return [result.minValue - 0.5, result.maxValue + 0.5];
            } else {
                return null;
            }
        });
        setData(newData);
    }, [
        calculator.comparisonResult,
        calculator.results,
        calculator.threshold,
        calculator.variants,
    ]);

    const chartData = useMemo(
        () => ({
            labels,
            datasets: [
                {
                    backgroundColor,
                    barPercentage: 1,
                    borderRadius: 40,
                    borderSkipped: false,
                    data,
                    label: 'Visualization',
                    maxBarThickness: 50,
                    minBarLength: 10,
                },
            ],
        }),
        [labels, data, backgroundColor]
    );

    const chartOptions = useMemo(
        () => ({
            animation: {
                duration: 300,
            },
            maintainAspectRatio: false,
            responsive: true,
            plugins: {
                legend: {
                    display: false,
                },
                tooltip: {
                    enabled: false,
                },
                annotation: {
                    annotations:
                        calculator.threshold != null && Number.isFinite(calculator.threshold)
                            ? {
                                thresholdLine: {
                                    type: 'line',
                                    yMin: calculator.threshold,
                                    yMax: calculator.threshold,
                                    borderColor: '#F5826B',
                                    borderWidth: 3,
                                },
                            }
                            : {},
                },
            },
            scales: {
                x: {
                    grid: {
                        display: false,
                    },
                    ticks: {
                        font: {
                            size: 15,
                            weight: 'bold',
                        },
                    },
                },
                y: {
                    ticks: {
                        stepSize: 5,
                        font: {
                            size: 15,
                        },
                        callback: (value: number | string) => String(value) + '%',
                    },
                },
            },
        }),
        [calculator.threshold]
    );

    const containerClasses = clsx('position-relative', className);

    const containerStyles = {
        height,
    };

    return <div className={containerClasses} style={containerStyles}>
        <Bar
            data={chartData}
            options={chartOptions}
        />
    </div>;
}
